import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, IconButton, Tooltip, Stack, Switch, Skeleton, Paper,
  TablePagination, alpha, useTheme,
} from '@mui/material';
import { Edit, Delete, Visibility, Inbox, CardMembership } from '@mui/icons-material';
import type { OperationalZoneRow } from '../../services/zoneService';
import { zoneSupportsPartnerSubscription } from '../../utils/partnerZoneUtils';
import { formatZoneDisplayName, formatZoneLevelLabel } from '../../pages/zones/zoneTypes';

interface ZoneRecordTableProps {
  rows: OperationalZoneRow[];
  loading?: boolean;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onView: (row: OperationalZoneRow) => void;
  onEdit?: (row: OperationalZoneRow) => void;
  onDelete?: (row: OperationalZoneRow) => void;
  onToggleActive?: (row: OperationalZoneRow, isActive: boolean) => void;
  onManageSubscriptions?: (row: OperationalZoneRow) => void;
  allowMutations?: boolean;
}

export default function ZoneRecordTable({
  rows,
  loading,
  page = 0,
  pageSize = 25,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onManageSubscriptions,
  allowMutations = true,
}: ZoneRecordTableProps) {
  const theme = useTheme();
  const canMutate = allowMutations && Boolean(onEdit && onDelete && onToggleActive);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={52} sx={{ mx: 2, my: 0.5 }} />)}
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed',
          py: 6,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Inbox sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700} color="text.secondary">No zones found</Typography>
        <Typography variant="body2" color="text.disabled">Add a zone to configure coverage and pricing</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 56 }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Zone</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', sm: 'table-cell' } }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', md: 'table-cell' } }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', lg: 'table-cell' } }}>Country</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', lg: 'table-cell' } }}>Services</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 52 }}>Subs</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 80 }}>Active</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 148 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const serialNo = page * pageSize + index + 1;
              const serviceCount = Array.isArray(row.serviceIds) ? row.serviceIds.length : 0;
              const subscriptionZone = zoneSupportsPartnerSubscription(row.metadata as Record<string, unknown> | undefined);
              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                  onClick={() => onView(row)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">{serialNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {row.color && (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color, flexShrink: 0 }} />
                      )}
                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={700} noWrap>{formatZoneDisplayName(row.name)}</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                          <Chip size="small" label={formatZoneLevelLabel(row.zoneLevel)} variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                          {row.biddingEnabled && <Chip size="small" label="Bidding" color="primary" sx={{ height: 18, fontSize: '0.62rem' }} />}
                          {subscriptionZone && <Chip size="small" label="Subscription" color="secondary" sx={{ height: 18, fontSize: '0.62rem' }} />}
                        </Stack>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary">{row.code}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="caption">{formatZoneLevelLabel(row.zoneLevel)}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="caption">{row.country || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Chip size="small" label={serviceCount} variant="outlined" sx={{ height: 20, minWidth: 28 }} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {subscriptionZone ? (
                      <Tooltip title="Manage partner subscription plans">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onManageSubscriptions?.(row)}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                          }}
                        >
                          <CardMembership fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {canMutate ? (
                      <Switch
                        size="small"
                        checked={!!row.isActive}
                        onChange={(e) => onToggleActive!(row, e.target.checked)}
                      />
                    ) : (
                      <Chip size="small" label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'success' : 'default'} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" justifyContent="flex-end">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => onView(row)}><Visibility fontSize="small" /></IconButton>
                      </Tooltip>
                      {canMutate && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit!(row)}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete!(row)}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && onPageSizeChange && (
        <TablePagination
          component="div"
          count={totalCount ?? rows.length}
          page={page}
          onPageChange={(_, next) => onPageChange(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      )}
    </Paper>
  );
}
